/* eslint-disable import/prefer-default-export */
import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import { SPEND_DEFERRED } from '../../constants/ach/apiUrls';
import {
  API_CONTRACT_CAMPAIGNS_URL,
  API_CONTRACT_CTR_DATA_URL,
  API_CONTRACT_IO_DATA_URL,
  API_CONTRACT_KPI_OPTIONS_URL,
  API_CONTRACT_LATEST_REPORT_URL,
  API_CONTRACT_PACING_DATA_URL,
  API_CONTRACT_SUMMARY_DATA_URL,
  API_CONTRACT_TARGETED_CREATIVES_URL,
  API_CONTRACT_URL,
  API_CONTRACTS_URL,
  API_COPY_CONTRACT_URL,
  API_PASTE_CONTRACT_URL,
  API_PAUSE_ALL_CONTRACT_URL,
  API_RESUME_ALL_CONTRACT_URL,
  API_VERIONS_CONTRACTS_URL,
  API_VERIONS_META_CONTRACTS_URL,
} from '../../constants/apiUrls';
import { getRequestJSON, postRequestJSON } from '../../util/services';

export const getNew = (apiHost: string): Promise<any> => getRequestJSON(apiHost, '/admin/contracts/new');

export const getAll = (
  apiHost: string,
  page,
  perPage: number,
  filterQuery,
  sort,
  direction,
  topAdvertisers = false
): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACTS_URL, {
      '*': `?${queryString.stringify({
        pacing: true,
        filters: filterQuery || undefined,
        page: page + 1,
        per_page: perPage.toString(),
        sort,
        direction,
        top_advertisers: topAdvertisers || undefined,
        // ACH-bj8d: skip the inline BigQuery spend stitch; the grid lazy-loads it
        // via useContractsPacing. Omitted (and inline again) when SPEND_DEFERRED
        // is undefined. Ignored by Rails when contracts is unflipped.
        spend: SPEND_DEFERRED,
      })}`,
    })
  );

export const get = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    // ACH-bj8d: show carries no lazy-load companion — no consumer reads the deferred
    // spend scalars (budget_spent_monthly_summary / spend_goal_to_date).
    `${generatePath(API_CONTRACT_URL, { contractId: contractId?.toString() })}${
      SPEND_DEFERRED ? `?${queryString.stringify({ spend: SPEND_DEFERRED })}` : ''
    }`
  );

export const getMultiple = (apiHost: string, contractIdList: number[]): Promise<any> =>
  new Promise((resolve, reject) => {
    const returnValue = [];
    const getMultipleHelper = async () => {
      contractIdList.forEach(async (contractId, index) => {
        try {
          const contract = await get(apiHost, contractId);
          returnValue.push(contract);

          if (index === contractIdList.length - 1) {
            resolve(returnValue);
          }
        } catch (e) {
          reject(e);
        }
      });
    };

    getMultipleHelper();
  });

export const getVersions = (
  apiHost: string,
  contractId: number,
  page: number,
  perPage: number,
  filters: Object = {}
): Promise<any> =>
  new Promise((resolve, reject) => {
    const getVersionsAsyncHelper = async () => {
      const queryParams = `?${queryString.stringify({
        ...filters,
        model_type: 'Contract',
        page: page + 1,
        per_page: perPage.toString(),
      })}`;
      const versions = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_CONTRACTS_URL, {
          contractId: contractId.toString(),
          '*': queryParams,
        })
      );

      const meta = await getRequestJSON(
        apiHost,
        generatePath(API_VERIONS_META_CONTRACTS_URL, {
          contractId: contractId.toString(),
        })
      );

      if (versions && meta) {
        resolve({
          ...versions,
          meta,
        });
      } else {
        reject();
      }
    };

    getVersionsAsyncHelper();
  });

export const copy = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_COPY_CONTRACT_URL, {
      contractId: contractId.toString(),
    })
  );

export const paste = (apiHost: string, contractId: number, payload): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_PASTE_CONTRACT_URL, {
      contractId: contractId.toString(),
    }),
    payload
  );

export const getLatestReport = (apiHost: string, contractId: number, type: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_LATEST_REPORT_URL, {
      contractId: contractId.toString(),
      '*': `?${queryString.stringify({
        type: type.toString(),
      })}`,
    })
  );

export const pauseAll = (apiHost: string, contractId: number): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_PAUSE_ALL_CONTRACT_URL, {
      contractId: contractId.toString(),
    }),
    undefined
  );

export const resumeAll = (apiHost: string, contractId: number): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_RESUME_ALL_CONTRACT_URL, {
      contractId: contractId.toString(),
    }),
    undefined
  );

export const getPacingData = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_PACING_DATA_URL, {
      contractId: contractId.toString(),
    })
  );

export const getCtrData = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_CTR_DATA_URL, {
      contractId: contractId.toString(),
    })
  );

export const getSummaryData = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_SUMMARY_DATA_URL, {
      contractId: contractId.toString(),
    })
  );

export const getLineItemData = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_IO_DATA_URL, {
      contractId: contractId.toString(),
    })
  );

export const getCampaigns = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_CAMPAIGNS_URL, {
      contractId: contractId.toString(),
    })
  );

export const getTargetedCreatives = (apiHost: string, contractId: number): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_CONTRACT_TARGETED_CREATIVES_URL, {
      contractId: contractId.toString(),
    })
  );

export const getKpiOptions = (apiHost: string): Promise<any> => getRequestJSON(apiHost, API_CONTRACT_KPI_OPTIONS_URL);
